import React, { useState } from "react";
import { ILesson } from "src/types";
import showToast from "@utils/toast";
import { API_URL } from "src/service/config";
import fetchWithToken from "@utils/tokenUtils";

interface Props {
  lesson: ILesson | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditLessonModal = ({ lesson, onClose }: Props) => {
  const [formData, setFormData] = useState({
    title: lesson?.title || "",
    description: lesson?.description ?? "",
    available: lesson?.available ?? true,
    goal: lesson?.goal ?? "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!lesson) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    const passedInputValues =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: passedInputValues,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        available: formData.available,
        goal: formData.goal,
      };

      const res = await fetchWithToken(`${API_URL}/lessons/${lesson.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok) {
        showToast("Lesson updated successfully", true);
        onClose();
        window.location.reload();
      } else {
        showToast(result.message || "Update failed", false);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err?.message
          : "There was an error saving lesson!";
      showToast(message, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-6 shadow-2xl relative border border-gray-200">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-lg"
        >
          ✕
        </button>

        <h2 className="text-base font-bold text-gray-800">
          Edit Lesson: <span className="text-brand">{lesson.title}</span>
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Lesson Title
            </label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g. Yoruba Essentials"
              className="input-style"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Lesson Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              cols={4}
              placeholder="Lesson description"
              className="input-style"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 font-medium block mb-1">
              Lesson Goal
            </label>
            <input
              name="goal"
              value={formData.goal}
              onChange={handleChange}
              required
              placeholder="Lesson Goal"
              className="input-style"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              name="available"
              checked={formData.available}
              onChange={handleChange}
              className="scale-90 text-emerald-600"
            />
            <label className="text-sm text-gray-700">Available</label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="button-primary w-full"
          >
            {isSubmitting ? "Updating..." : "Update Lesson"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditLessonModal;
